import { Alerta, Badge, Button, Casilla, Dialog, ListaOrdenable, Row, Select, Stack, TextArea, TextField } from "@/components";
import type { PasoManual } from "@/lib/api";
import { faltaDato, QUIENES, textoDatos, textoProgreso, valorDato } from "@/lib/manual";
import type { PasosManual } from "./usePasosManual";

interface Props {
  m: PasosManual;
  itemId: number;
  nombre: string;
}

/** El checklist del manual de una subcategoría (arriba de sus presupuestos): cada paso con los datos que necesita. */
export function PasosSubcategoria({ m, itemId, nombre }: Props) {
  const pasos = m.pasosDe(itemId);
  const progreso = textoProgreso(pasos);
  if (pasos.length === 0 && !m.puedeEditar) return null;
  return (
    <Stack espacio={2}>
      <Row justificar="entre" envolver espacio={2}>
        <h3 className="font-semibold">
          Pasos{progreso && <span className="ml-2 text-chico font-normal text-texto-suave">{progreso}</span>}
        </h3>
        {m.puedeEditar && pasos.length > 1 && (
          <Button variante="fantasma" tamanio="sm" onClick={() => m.alternarOrden(itemId)}>
            {m.ordenando === itemId ? "Listo" : "Ordenar pasos"}
          </Button>
        )}
      </Row>
      {m.ordenando === itemId ? (
        <ListaOrdenable etiqueta={`Pasos de ${nombre}`} elementos={pasos.map((p) => ({ id: p.id, texto: p.texto }))} onMover={(ids) => m.mover(itemId, ids)} />
      ) : (
        pasos.length > 0 && (
          <ol aria-label={`Pasos de ${nombre}`} className="divide-y divide-borde rounded-tarjeta border border-borde">
            {pasos.map((p) => (
              <Paso key={p.id} m={m} p={p} />
            ))}
          </ol>
        )
      )}
      {pasos.length === 0 && <p className="text-chico text-texto-suave">Todavía no tiene pasos.</p>}
      {m.puedeEditar && (
        <div>
          <Button variante="secundario" tamanio="sm" aria-label={`Agregar paso a ${nombre}`} onClick={() => m.abrirNuevo(itemId)}>
            Agregar paso
          </Button>
        </div>
      )}
    </Stack>
  );
}

function Paso({ m, p }: { m: PasosManual; p: PasoManual }) {
  const faltan = p.datos.some((d) => !valorDato(d)) || faltaDato(p);
  return (
    <li className="px-3 py-2">
      <Stack espacio={1}>
        {m.puedeEditar ? (
          <Casilla etiqueta={p.texto} marcado={p.hecho_en !== null} onCambio={(hecho) => m.tildar(p, hecho)} />
        ) : (
          <p className={p.hecho_en ? "text-texto-suave line-through" : ""}>{p.texto}</p>
        )}
        <Row envolver espacio={2}>
          {p.quien && <Badge tono="neutro">{p.quien}</Badge>}
          {p.datos.length > 0 && <Badge tono={faltan ? "alerta" : "exito"}>{textoDatos(p)}</Badge>}
          {p.pide_dato && (p.respuesta ? <span className="text-chico">{`Respuesta: ${p.respuesta}`}</span> : <Badge tono="alerta">Falta responder</Badge>)}
        </Row>
        {p.datos.length > 0 && (
          <ul aria-label={`Datos para ${p.texto}`} className="ml-1 text-chico">
            {p.datos.map((d, i) => {
              const valor = valorDato(d);
              return (
                <li key={i}>
                  {`• ${d.nombre}`}
                  {d.quien && <span className="text-texto-suave">{` (${d.quien})`}</span>}
                  {": "}
                  {valor ? (
                    <span className="font-medium">{valor}</span>
                  ) : (
                    <span className="text-alerta">{d.variable ? "falta (cargalo en Terreno o Unidades)" : "falta"}</span>
                  )}
                  {!d.valor && d.valor_sistema && <span className="text-texto-suave"> · del sistema</span>}
                </li>
              );
            })}
          </ul>
        )}
        {p.detalle && <p className="text-chico whitespace-pre-line text-texto-suave">{p.detalle}</p>}
        {m.puedeEditar && (
          <Row espacio={2}>
            <Button variante="fantasma" tamanio="sm" aria-label={`${faltan ? "Completar datos de" : "Editar"} ${p.texto}`} onClick={() => m.abrirEditar(p)}>
              {faltan ? "Completar datos" : "Editar"}
            </Button>
            <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${p.texto}`} onClick={() => m.abrirBorrar(p)}>
              Borrar
            </Button>
          </Row>
        )}
      </Stack>
    </li>
  );
}

/** Los diálogos de los pasos (uno para toda la pantalla). */
export function DialogosPasos({ m }: { m: PasosManual }) {
  const d = m.dialogo;
  const opciones = (actual: string) => [...QUIENES, ...(actual && !QUIENES.includes(actual) ? [actual] : [])].map((q) => ({ valor: q, etiqueta: q }));
  return (
    <>
      <Dialog abierto={d?.tipo === "form"} onCambio={(abierto) => !abierto && m.cerrar()} titulo={d?.tipo === "form" && d.paso ? "Editar paso" : "Nuevo paso"}>
        <form onSubmit={(e) => void m.guardar(e)} noValidate>
          <Stack espacio={4}>
            <TextArea etiqueta="Paso" value={m.form.texto} onChange={(e) => m.cambiar("texto", e.target.value)} error={m.errores.texto} filas={2} />
            <Select etiqueta="Quién lo hace" opciones={opciones(m.form.quien)} valor={m.form.quien || undefined} onCambio={(v) => m.cambiar("quien", v)} />
            {m.form.pide_dato && <TextField etiqueta="Respuesta" value={m.form.respuesta} onChange={(e) => m.cambiar("respuesta", e.target.value)} error={m.errores.respuesta} />}
            <Stack espacio={2}>
              <h3 className="font-semibold">Datos que hacen falta</h3>
              {m.form.datos.map((dato, i) => (
                <div key={i} className="rounded-control border border-borde p-2">
                  <Stack espacio={2}>
                    <TextField etiqueta={`Dato ${i + 1}`} value={dato.nombre} onChange={(e) => m.cambiarDato(i, "nombre", e.target.value)} />
                    <Row envolver espacio={2} alinear="fin">
                      <Select etiqueta={`Quién lo sabe ${i + 1}`} opciones={opciones(dato.quien)} valor={dato.quien || undefined} onCambio={(v) => m.cambiarDato(i, "quien", v)} className="min-w-40 flex-1" />
                      <TextField
                        etiqueta={`Valor ${i + 1}`}
                        value={dato.valor}
                        placeholder={dato.valor_sistema ?? undefined}
                        ayuda={dato.valor_sistema ? `Del sistema: ${dato.valor_sistema}` : undefined}
                        onChange={(e) => m.cambiarDato(i, "valor", e.target.value)}
                        contenedorClassName="min-w-40 flex-1"
                      />
                      <Button variante="fantasma" tamanio="sm" aria-label={`Quitar el dato ${i + 1}`} onClick={() => m.quitarDato(i)}>
                        Quitar
                      </Button>
                    </Row>
                  </Stack>
                </div>
              ))}
              <Alerta texto={m.errores.datos} />
              <div>
                <Button variante="secundario" tamanio="sm" onClick={m.agregarDato}>
                  Agregar dato
                </Button>
              </div>
            </Stack>
            <TextArea etiqueta="Detalle (por qué, qué tener en cuenta)" value={m.form.detalle} onChange={(e) => m.cambiar("detalle", e.target.value)} error={m.errores.detalle} filas={4} />
            <Alerta texto={m.errorGuardar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={m.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={m.guardando}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "copiar"} onCambio={(abierto) => !abierto && m.cerrar()} titulo="Copiar de otro desarrollo">
        <Stack espacio={4}>
          <p className="text-chico text-texto-suave">
            Trae las categorías y subcategorías que falten (sin precios ni cantidades) y los pasos de las subcategorías que todavía no tienen ninguno, sin tildes ni datos anotados. Lo que ya está no se toca.
          </p>
          <Select etiqueta="Desarrollo" opciones={m.opcionesDesarrollo} valor={m.desde} onCambio={m.setDesde} />
          <Alerta texto={m.errorCopiar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={m.cerrar}>
              Cancelar
            </Button>
            <Button cargando={m.copiando} disabled={!m.desde} onClick={() => void m.copiar()}>
              Copiar
            </Button>
          </Row>
        </Stack>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && m.cerrar()} titulo="Borrar paso">
        <Stack espacio={4}>
          <p>{d?.tipo === "borrar" ? `¿Borrar el paso «${d.paso.texto}»?` : ""}</p>
          <Alerta texto={m.errorBorrar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={m.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={m.borrando} onClick={() => void m.confirmarBorrar()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </>
  );
}
